import { activeSessionIdStore, sessionsStore, changeToSession } from '@customerio/hydra-session-state';
import { WORKSPACE_MISMATCH_SNACKBAR_KEY } from '@/features/account-switcher/constants';
import { getWorkspaceIdFromCurrentUrl } from '@/utils/workspace-id';

/**
 * When the auth call fails and the URL contains a workspace ID, this function
 * checks whether a different session has access to that workspace.
 *
 * Call this when auth has failed (auth.data is null) after checking for expired
 * session fallback. It will either:
 * - Switch to the session that owns the workspace and reload to the same URL (returns true)
 * - Redirect to `/` with a snackbar when no session has access (returns true)
 * - Return false if there's no workspace ID in the URL, allowing the normal
 *   login redirect to proceed.
 */
export function handleWorkspaceSessionMismatch(): boolean {
  const workspaceId = getWorkspaceIdFromCurrentUrl();
  if (workspaceId == null) {
    return false;
  }

  const allSessions = sessionsStore.get();
  if (allSessions.length === 0) {
    return false;
  }

  // Check if the current session already has access to this workspace.
  // If it does, the auth failure is for a different reason (e.g. expired cookie),
  // so we should not intervene.
  const currentSessionId = activeSessionIdStore.get();
  const currentSession = allSessions.find((session) => session.id === currentSessionId);
  if (currentSession != null && currentSession.workspace_ids.includes(workspaceId)) {
    return false;
  }

  // Find a session that has access to the workspace in the URL
  const matchingSession = allSessions.find((session) => session.workspace_ids.includes(workspaceId));

  if (matchingSession != null) {
    // Switch to the matching session and reload to the same URL
    changeToSession(matchingSession.id);
    window.location.assign(window.location.href);
    return true;
  }

  // No session has access to this workspace — redirect to `/` and show a snackbar
  sessionStorage.setItem(WORKSPACE_MISMATCH_SNACKBAR_KEY, 'true');
  window.location.assign('/');
  return true;
}
